/**
 * @important - typing for nuxt plugins is in index.d.ts
 */

import type { StoryBlokLink } from '#constants/types/storyblok';
import { useGlobalContentStore } from '~/store/globalContent';
import useLinkReplacer from '~/composables/useLinkReplacer';


export default defineNuxtPlugin(() => {
  return {
    provide: {
      squareMeterFormatting(input: string | null | undefined): string {
        if (!input) {
          return '';
        }
        return input.replace(/([Mm])2(?=\s|$)/g, '$1²');
      },
      /**
       *
       * @param input
       * @param strict - Storyblok sometimes keeps cached_url in draft mode, even when link is removed
       */
      sbLinkCheck(input: StoryBlokLink, strict = false): boolean {
        if ((input?.story?.url && input?.story.url !== '') || (input?.cached_url !== '' && !strict) || input?.url !== '') {
          return true;
        }
        return false;
      },
      manualUrl(url: string|null|undefined): string {
        const { manualUrl } = useLinkReplacer();
        return manualUrl(url);
      },
      sbLinkFix(input: StoryBlokLink|string): string {
        if (!input) {
          return '';
        }
        const globalContent = useGlobalContentStore();
        if (typeof input === 'string') {
          return globalContent.isSubFolder ? `/${input}` : input;
        }

        if (input.url.startsWith('tel:') || input.url.startsWith('sms:')|| input.url.startsWith('mailto:')) {
          return input.url;
        }

        let output = '';
        if (input.story?.url && input.story.url !== '') {
          output = input.story?.url;
        }
        if (input.cached_url !== '') {
          output = input.cached_url;
        }
        if (input.url !== '') {
          output = input.url;
        }

        // Check if is should be a internal link but domain was included
        // This will only work we are in the same environment as the link was created in
        if (globalContent.domainMap?.url && output.startsWith(globalContent.domainMap.url)) {
          const toBeRemoved = globalContent.domainMap.url.length - globalContent.domainMap.storyblokBasePath.length - 1;
          output = output.slice(toBeRemoved);
        }

        // AT uses synced content from DE in Storyblok - replace /de/ links with /at/ when on AT
        if (input.linktype === 'story' && globalContent.getStoryblokBasePath === 'at' && output.startsWith('de/')) {
          output = output.replace('de/', 'at/');
        }

        if (input.linktype === 'email') {
          return `mailto:${output}`;
        }
        if (!globalContent.isSubFolder ||
        input.linktype === 'url' ||
        input.linktype === 'asset') {
          if (!output.startsWith('http') && !output.startsWith('/')) {
            return `/${output}`;
          }
          return output;
        }

        // Links that cross into another market's subfolder (e.g. fi/... while browsing global/...)
        // must be returned as an absolute URL, so NuxtLink treats them as external and does a real
        // browser navigation instead of an SPA route change. A client-side nav never re-runs the
        // server-only market/locale init, leaving the Algolia index, currency, flag etc. stuck on
        // the old market.
        const [leadingSegment] = output.split('/');
        if (globalContent.domainMap?.url && leadingSegment && leadingSegment !== globalContent.getStoryblokBasePath) {
          const { origin } = new URL(globalContent.domainMap.url);
          return `${origin}/${output}`;
        }

        return `/${output}`;
      },
      sbLinkTarget(input: StoryBlokLink): string|null {
        const globalContent = useGlobalContentStore();

        if (!input) {
          return null;
        }
        if (input.target && input.target.trim() !== '') {
          return input.target;
        }

        // If it's a local link written as a full url
        if (globalContent.domainMap?.url && input.url.startsWith(globalContent.domainMap.url)) {
          return null;
        }

        if (input.linktype === 'asset') {
          return '_blank';
        }
        if (input.linktype === 'url' && input.url.startsWith('http')) {
          return '_blank';
        }
        return null;
      },
      toNumber(value: string): number {
        try {
          const parsedString = parseFloat(value.replace(/\s/g, '').replace(/,/g, ''));
          return parsedString;
        } catch (e) {
          console.warn(e);
          // @ts-ignore
          return value;
        }
      },
      /**
       * Check if a storyblok rich text actually have any content and not just an empty <p> that seems to be default
       */
      sbRichTextHasContent(input: { type: string; content?: { type: string; content?: unknown[] }[] } | null | undefined): boolean {
        if (!input?.content?.length) return false;
        return input.content.some(node => node.content && node.content.length > 0);
      },
      /**
       * Fixes javascript floating point issues
       * @example $floatingPoint(79-63.2)
       * From this answer
       * @see https://github.com/maslianok/js-floating-point/blob/master/src/index.js
       */
      floatingPoint(value: any, recurringSymbols = 6) {
        if (!value || Number.isNaN(parseFloat(value))) {
          // value is wrong or empty
          return value;
        }

        const [intPart, decimalPart] = `${value}`.split('.');

        if (!decimalPart) {
          // no decimal part
          return value;
        }

        const regex = new RegExp(
          `(9{${recurringSymbols},}|0{${recurringSymbols},})(\\d)*$`,
          'gm'
        );
        const matched = decimalPart.match(regex);

        if (!matched) {
          // no floating-point bug
          return value;
        }

        const [wrongPart] = matched;
        const correctDecimalsLength = decimalPart.length - wrongPart.length;
        return parseFloat(
          parseFloat(`${intPart}.${decimalPart}`).toFixed(correctDecimalsLength)
        );
      },

    },
  };
});
